"use client";

import React from "react";

import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

interface BookingResponse {
  id: number;
  title?: string;
  service?: string;
  package_item?: { name: string };
  services?: Array<{ date_of_service: string }>;
  deceased?: Array<{
    first_name?: string;
    surname?: string;
    person_image?: string;
  }>;
}

interface PaginatedResponse<T> {
  results: T[];
  count: number;
}

interface UseBookingsProps {
  venueId: string | undefined;
  apiService: {
    get: <T>(url: string) => Promise<{ data: T }>;
  };
}

interface UseBookingsReturn {
  bookings: BookingResponse[];
  totalBookings: number;
  bookingsLoading: boolean;
  page: number;
  rowsPerPage: number;
  handleChangePage: (event: unknown, newPage: number) => void;
  handleChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export const useBookings = ({
  venueId,
  apiService,
}: UseBookingsProps): UseBookingsReturn => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [totalBookings, setTotalBookings] = useState(0);
  const [bookings, setBookings] = useState<BookingResponse[]>([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);

  const fetchBookings = useCallback(
    async (pageNumber: number, pageSize: number) => {
      try {
        setBookingsLoading(true);
        const response = await apiService.get<
          PaginatedResponse<BookingResponse>
        >(
          `/api/bookings/booking/?venue_id=${venueId}&page=${
            pageNumber + 1
          }&page_size=${pageSize}`,
        );

        setBookings(response.data.results || []);
        setTotalBookings(response.data.count || 0);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load bookings");
      } finally {
        setBookingsLoading(false);
      }
    },
    [venueId, apiService],
  );

  useEffect(() => {
    if (venueId) {
      fetchBookings(page, rowsPerPage);
    }
  }, [fetchBookings, venueId, page, rowsPerPage]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return {
    bookings,
    totalBookings,
    bookingsLoading,
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
  };
};

export default useBookings;
